import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Search, Check, MapPin, X, BellRing } from 'lucide-react';
import { Button } from './ui/button';
import { useNotifications } from '../contexts/NotificationContext';
import { API } from '../App';

/**
 * TownFollowPicker — modal for following one or more towns to receive
 * Safety Bulletin alerts. Searchable, multi-select. Auto-provisions a
 * device subscription if the user hasn't set up push/email yet.
 *
 * Props:
 *   open:        boolean
 *   onClose:     () => void
 *   title?:      string   — optional heading override
 *   subtitle?:   string   — optional description override
 *   focusTown?:  string   — scroll/highlight a specific town on open
 */
const TownFollowPicker = ({ open, onClose, title, subtitle, focusTown }) => {
  const { t } = useTranslation();
  const { preferences, toggleArea } = useNotifications();
  const followed = preferences.areaSubscriptions || [];

  const [towns, setTowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/safety-bulletin/towns`);
        if (!cancelled) setTowns((r.data.towns || []).slice().sort((a, b) => a.localeCompare(b)));
      } catch {
        if (!cancelled) setTowns([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    if (!q.trim()) return towns;
    const needle = q.trim().toLowerCase();
    return towns.filter(t => t.toLowerCase().includes(needle));
  }, [q, towns]);

  const onToggle = async (town) => {
    if (busy) return;
    setBusy(true);
    try { await toggleArea(town); } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[100]"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      data-testid="town-follow-picker"
    >
      <div
        className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-200">
          <div className="flex items-start gap-3">
            <div className="bg-purple-100 rounded-lg p-2 flex-shrink-0">
              <BellRing className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {title || t('safety.follow.pickerTitle', 'My safety alerts')}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {subtitle || t('safety.follow.pickerSubtitle', "Pick one or more towns. You'll be notified when a safety alert is posted there.")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 -mt-1"
            data-testid="town-picker-close"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('safety.follow.searchPlaceholder', 'Search towns…')}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              data-testid="town-picker-search"
              autoFocus={!focusTown}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2" data-testid="town-picker-count">
            {t('safety.follow.followingCount', { count: followed.length, defaultValue: 'Following {{count}} town(s)' })}
          </p>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-3" data-testid="town-picker-list">
          {loading ? (
            <div className="py-8 text-center text-gray-400 text-sm">…</div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm" data-testid="town-picker-empty">
              {t('safety.follow.noResults', 'No towns match your search.')}
            </div>
          ) : (
            <ul className="space-y-1.5">
              {filtered.map((town) => {
                const isOn = followed.includes(town);
                const isFocus = focusTown && town === focusTown;
                return (
                  <li key={town}>
                    <button
                      onClick={() => onToggle(town)}
                      disabled={busy}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border text-sm text-left transition-colors ${
                        isOn
                          ? 'bg-purple-50 border-purple-300 text-purple-900'
                          : isFocus
                            ? 'bg-amber-50 border-amber-300 text-gray-900 hover:bg-amber-100'
                            : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50'
                      }`}
                      data-testid={`town-picker-option-${town}`}
                    >
                      <span className="flex items-center gap-2 truncate">
                        <MapPin className={`h-4 w-4 flex-shrink-0 ${isOn ? 'text-purple-600' : 'text-gray-400'}`} />
                        <span className="truncate">{town}</span>
                      </span>
                      {isOn && (
                        <span className="flex items-center gap-1 text-xs font-medium text-purple-700 flex-shrink-0">
                          <Check className="h-4 w-4" />
                          {t('safety.follow.followingBadge', 'Following')}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl sm:rounded-b-2xl">
          <p className="text-[11px] text-gray-500 mb-2 leading-snug">
            {t('safety.follow.channelsNote', 'Alerts come through push and your in-app inbox. Email stays off unless you turn it on in notification settings.')}
          </p>
          <Button
            onClick={onClose}
            className="w-full bg-slate-800 hover:bg-slate-900 text-white"
            data-testid="town-picker-done"
          >
            {t('safety.follow.doneBtn', 'Done')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TownFollowPicker;
